import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { ChannelBadge } from "@/components/ChannelBadge";
import { toast } from "sonner";
import { Plus, Trash, X } from "@phosphor-icons/react";
import { channelButtonClass } from "@/lib/ui-helpers";

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", channel: "whatsapp", body: "" });

  const load = useCallback(async () => {
    const { data } = await api.get("/templates");
    setTemplates(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const variables = Array.from(form.body.matchAll(/\{\{(\w+)\}\}/g)).map((m) => m[1]);
      await api.post("/templates", { ...form, variables });
      toast.success("Template created");
      setForm({ name: "", channel: "whatsapp", body: "" });
      setShowForm(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete template?")) return;
    await api.delete(`/templates/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <Layout>
      <div className="border-b border-white/10 px-10 py-8 flex items-end justify-between">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">/// snippets</div>
          <h1 className="font-heading text-5xl tracking-tight mt-2">Templates</h1>
        </div>
        <button data-testid="template-new-button" onClick={() => setShowForm(true)}
          className="bg-white text-black px-4 py-2.5 rounded-sm flex items-center gap-2 hover:bg-white/90 transition-colors">
          <Plus size={16} weight="bold" /> New template
        </button>
      </div>

      <div className="p-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.length === 0 && (
          <div className="col-span-full text-center py-16 text-white/40 font-mono text-sm border border-dashed border-white/10">
            no templates yet
          </div>
        )}
        {templates.map((t) => (
          <div key={t.id} data-testid={`template-card-${t.id}`}
            className="border border-white/10 bg-[#121212] p-5 hover:bg-[#161616] transition-colors group">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-heading text-xl tracking-tight">{t.name}</div>
                <div className="mt-1"><ChannelBadge channel={t.channel} size="xs" /></div>
              </div>
              <button data-testid={`template-delete-${t.id}`} onClick={() => remove(t.id)}
                className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-white/10 rounded-sm text-white/40 hover:text-[#E1306C] transition-all">
                <Trash size={14} />
              </button>
            </div>
            <p className="text-sm text-white/70 whitespace-pre-wrap line-clamp-6">{t.body}</p>
            {t.variables?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/5 flex gap-1 flex-wrap">
                {t.variables.map((v) => (
                  <span key={v} className="text-[10px] font-mono px-1.5 py-0.5 border border-white/10 rounded-sm">{`{{${v}}}`}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A0A0A] border border-white/15 w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">/// new</div>
                <div className="font-heading text-2xl tracking-tight">New template</div>
              </div>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-white/10 rounded-sm">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={create} className="p-6 space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Name</label>
                <input data-testid="template-form-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required
                  className="w-full mt-1 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-2.5 outline-none transition-colors" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Channel</label>
                <div className="mt-1 flex gap-2">
                  {["whatsapp", "instagram"].map((c) => (
                    <button type="button" key={c} data-testid={`template-form-channel-${c}`}
                      onClick={() => setForm({...form, channel: c})}
                      className={`flex-1 px-3 py-2.5 border rounded-sm text-sm uppercase tracking-wider font-mono transition-colors ${channelButtonClass(form.channel, c)}`}
                    >{c}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">
                  Message body <span className="text-white/30">— use {"{{name}}"} for variables</span>
                </label>
                <textarea data-testid="template-form-body" value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} required rows={6}
                  placeholder="Hey {{name}}, thanks for reaching out!"
                  className="w-full mt-1 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-2.5 outline-none transition-colors font-mono text-sm" />
              </div>
              <button data-testid="template-form-submit" type="submit"
                className="w-full bg-white text-black py-2.5 rounded-sm font-medium hover:bg-white/90 transition-colors">
                Create template
              </button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
